/**
 * Mock data layer for the Users module.
 *
 * Regulated-industry user records for an on-premise AI platform serving
 * defense / healthcare / manufacturing customers (ITAR / CMMC / HIPAA / GDPR).
 *
 * Records include:
 *   - Identity & employment (email, employeeId, dept, manager, employment type, start/end dates)
 *   - Clearance & classification (ITAR §120.15 US Person verification, clearance levels,
 *     classification access, export control axes)
 *   - Healthcare (HIPAA workforce status, PHI access level, BAA on file)
 *   - Training & governance (certs, last review date, next review due, risk score)
 *   - Auth state (MFA, password age, active sessions)
 *   - Activity, audit trail, telemetry (per-user)
 *   - Invitations (pending queue)
 *
 * Derived from PaaS `RichUser` pattern (C:\Source\paas-ref\src\lib\mock-user-data.ts)
 * extended with ArgusAI regulated-industry fields.
 */

// THE lifecycle alphabet. Type-only: the screens name states with the shared
// declaration rather than a second union of their own.
import type { LifecycleState } from '@argusai/shared/schemas/user-lifecycle';
import {
  EMPLOYMENT_TYPE_LABELS,
  isTimeBoundedEmployment,
  type EmploymentType as SharedEmploymentType,
} from '@argusai/shared/schemas/user.schema';

// ─── Deterministic RNG ──────────────────────────────────────────────────────

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), t | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
const rng = mulberry32(137);
function randInt(min: number, max: number): number {
  return Math.floor(min + rng() * (max - min + 1));
}

const NOW = new Date('2026-04-20T12:00:00Z');
function daysAgo(n: number): Date {
  return new Date(NOW.getTime() - n * 24 * 60 * 60 * 1000);
}
function isoDay(d: Date): string {
  return d.toISOString();
}

// ─── Enums ──────────────────────────────────────────────────────────────────

/**
 * A user's lifecycle state, as the Users screens hold it.
 *
 * An ALIAS of the shared alphabet (`LifecycleStateSchema`), not a second list
 * (review B2aT2-R3). It used to be a hand-written five-value union, which
 * meant the two states B2a-T2 added to the table — `invited_revoked` and
 * `deleted` — had no name here, and `mapApiUser` folded both to `active`: a
 * revoked invitee wore a green "Active" badge while the lifecycle endpoint on
 * the same page reported `invited_revoked`. One noun, one alphabet.
 */
export type UserStatus = LifecycleState;

/**
 * THE employment type, re-exported from the one shared declaration — the same
 * move `UserStatus` above made, for the same reason (B2c-T2).
 *
 * It was a hand-written `'fte' | 'contractor' | 'vendor' | 'partner'`, a
 * SECOND vocabulary for a field the store already had one for
 * (`full_time | part_time | contractor | intern`), with two hand-written maps
 * bridging them: `apiToUiEmploymentType` in `use-users.ts` and
 * `employmentTypeMap` / `uiToApiEmploymentType` in the create and edit
 * dialogs. Neither map was injective, so the field did not round-trip — an
 * admin who picked "Vendor" got `contractor` stored and "Contractor" redrawn,
 * and `intern` was a value no screen could name.
 *
 * c32 widened the store's CHECK to the six values (adding `vendor` and
 * `partner`, which this file had been offering all along), and both maps are
 * deleted. The UI value IS the stored value.
 */
export type EmploymentType = SharedEmploymentType;

// Re-export the canonical clearance + HIPAA enums from @argusai/shared so all
// web code reads from one source. F61 (Wave 1a) aligned the values to the
// 5-level US Government scale; the legacy `none / public_trust / confidential
// / top_secret` strings have been removed.
//
// We import via the deep `/schemas/compliance.schema` path to avoid pulling
// the rest of the shared barrel (which transitively re-exports node:crypto
// helpers that the browser bundle cannot resolve).
import {
  ClearanceLevelSchema,
  CLEARANCE_LABELS,
  clearanceRequiresUsPerson,
  HipaaBaaStatusSchema,
  HIPAA_BAA_STATUS_LABELS,
  type ClearanceLevel,
  type HipaaBaaStatus,
} from '@argusai/shared/schemas/compliance.schema';
import type { ExportControl, Sensitivity } from '@argusai/shared';
// USR-F1-T1 — THE wire shape of a person's identity fields.
import type { UserIdentity } from '@argusai/shared/schemas/user-identity';

export {
  ClearanceLevelSchema,
  CLEARANCE_LABELS,
  clearanceRequiresUsPerson,
  HipaaBaaStatusSchema,
  HIPAA_BAA_STATUS_LABELS,
  type ClearanceLevel,
  type HipaaBaaStatus,
};

// The one classification alphabet (@argusai/shared document.schema.ts, LB3-T1).
export type ClassificationTier = Sensitivity;

export type ExportControlTier = ExportControl;

export type MfaMethod = 'none' | 'totp' | 'webauthn' | 'sms' | 'hardware_key' | 'email_otp';

export type UsPersonDocumentType =
  | 'us_passport'
  | 'drivers_license_ssn'
  | 'permanent_resident_card'
  | 'i_551'
  | 'birth_certificate'
  | 'naturalization';

/**
 * Canonical role-assignment source enum — shared between API parsing
 * (`useUserRoleAssignments` adapter) and badge rendering
 * (`RoleSourceBadge`). The set is the union of:
 *
 *  - DB role_source enum (packages/api/src/db/schema/roles.ts):
 *      'manual' | 'sso' | 'inherited'
 *  - Legacy mock-UI / synthesised values from the User adapter:
 *      'direct' | 'department' | 'user_group' | 'sso'
 *
 * Both API responses and synthesised system-role rows must produce a
 * value from this enum. The badge renders a label for every entry
 * here, plus a default fallback that prints the raw string verbatim
 * if the API ever introduces a new value the badge has not been
 * updated for yet (closes #181 — prior code crashed on 'manual').
 */
export type RoleSource =
  | 'direct'
  | 'department'
  | 'user_group'
  | 'sso'
  | 'manual'
  | 'inherited';

// ─── Supporting types ───────────────────────────────────────────────────────

export interface RoleAssignment {
  roleId: string;
  roleName: string;
  source: RoleSource;
  grantedAt: string;
  grantedBy?: string;           // userId of granter, or IdP name if sso
  sourceDetail?: string;        // e.g., "Inherited from department: Maintenance"
}

export interface ActiveSession {
  id: string;
  device: string;               // "MacBook Pro · Chrome 138"
  deviceType: 'desktop' | 'mobile' | 'tablet';
  ip: string;
  location: string;             // "Houston, TX, US"
  startedAt: string;
  lastActiveAt: string;
  current: boolean;
}

export interface TrainingCompletion {
  id: string;
  courseId: string;
  courseName: string;
  completedAt: string;
  expiresAt: string | null;
  certificateUrl: string | null;
  status: 'current' | 'expiring_soon' | 'expired';
}

// ─── Per-user activity / audit / telemetry ──────────────────────────────────

export interface UserActivityLog {
  id: string;
  timestamp: string;
  type:
    | 'login'
    | 'login_failed'
    | 'logout'
    | 'password_changed'
    | 'mfa_challenge'
    | 'mfa_enabled'
    | 'mfa_disabled'
    | 'impersonation_started'
    | 'impersonation_ended'
    | 'session_terminated';
  ip: string;
  userAgent: string;
  location: string;
  outcome: 'success' | 'failure';
  details: string;
}

export interface UserAuditLog {
  id: string;
  timestamp: string;
  sequenceNumber: number;
  hash: string;
  previousHash: string;
  action: string;                // "role_assigned", "clearance_granted", etc.
  actor: string;                 // userId of who performed the action
  actorName: string;
  target: string;                // what was changed
  before?: unknown;
  after?: unknown;
  reason?: string;
}

export interface UserTelemetry {
  queriesLast7d: number;
  queriesLast30d: number;
  avgQueriesPerDay: number;
  topAgentId: string;
  topAgentName: string;
  documentsAccessedLast30d: number;
  avgSessionDurationMin: number;
  lastActive: string;
  consumedTokensLast30d: number;
  estimatedCostLast30d: number;
}

// ─── Invitation ─────────────────────────────────────────────────────────────

export interface InvitationInfo {
  invitedAt: string;
  invitedBy: string;
  invitedByName: string;
  expiresAt: string;
  status: 'pending' | 'expired' | 'revoked';
  inviteLink: string;            // masked in UI
  preAssignedRoleIds: string[];
  preAssignedGroupIds: string[];
  preAssignedClearance: ClearanceLevel;
}

// ─── User ───────────────────────────────────────────────────────────────────

export interface User {
  // Identity
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;              // derived
  employeeId: string;
  phoneNumber: string | null;

  // Employment
  /**
   * B2b-T3 — THE DEPARTMENTS A PERSON IS IN, OR ALL OF THEM.
   *
   * The `department: string` (an id) / `departmentName: string` pair that stood
   * here is GONE. A person belongs to one or more departments or to all of them
   * (decisions.md, "Departments scope what people see"), and a pair of scalars
   * could only say the first of a set the store had already stopped limiting —
   * so the roster showed somebody in two departments as being in one, with no
   * error anywhere. This is the server's answer verbatim: the API's
   * `departments` object, rendered, never recomputed.
   *
   * `all` true means every department in the tenant, including ones created
   * tomorrow, and `items` is then empty. `all` false with an empty `items` is
   * "no department" — company-wide items only — and is NOT the same fact.
   */
  departments: { all: boolean; items: { id: string; name: string }[] };
  position: string;              // free-text duty title
  managerId: string | null;      // FK → User.id
  managerName: string | null;
  /**
   * B2c-T2 — NULLABLE, because the column is. `users.employment_type` allows
   * NULL for a row nobody has edited since bA1, and the adapter used to hide
   * that behind a `default: return 'fte'` in the deleted translation map: a
   * person whose employment type had never been set rendered as an FTE. A
   * screen renders no badge for null instead of asserting a fact the store
   * does not hold.
   */
  employmentType: EmploymentType | null;
  // Start/End date are YYYY-MM-DD strings or null — the API returns
  // null for legacy rows that pre-date the bA1 employment-fields
  // migration, and the adapter forwards null verbatim (see
  // `use-users.ts > normalizeDateOnly`). Render-side helpers
  // (`formatDateOnly`, `parseDateOnly`) are null-safe.
  startDate: string | null;
  endDate: string | null;        // contractors/vendors have end dates
  costCenter: string | null;

  // Status
  status: UserStatus;
  joinedDate: string;            // when first added to the platform
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;

  // Authentication
  /**
   * May the CALLER impersonate this person? Answered by the server
   * (`GET /users/:id` → `policy/impersonation.ts`), never derived here.
   * Optional because only the detail endpoint answers it; absent is `false`.
   */
  canImpersonate?: boolean;
  /**
   * May the CALLER transfer ownership of the tenant to this person? Answered
   * by the server (`GET /users/:id` → `policy/ownership.ts`), never derived
   * here — the question is about a role row the browser cannot see, and
   * `confirmPhrase` is the exact text the endpoint will check.
   * Optional because only the detail endpoint answers it; absent is refusal.
   */
  ownershipTransfer?: {
    allowed: boolean;
    reason: string | null;
    confirmPhrase: string;
  };
  mfaEnabled: boolean;
  mfaMethod: MfaMethod;
  mfaEnforcedByPolicy: boolean;
  passwordLastChangedAt: string | null;
  passwordExpiresAt: string | null;
  ssoLinked: boolean;
  ssoProvider: string | null;    // "Okta", "Azure Entra", "Google"

  // Access & roles
  roles: RoleAssignment[];
  groupIds: string[];
  activeSessions: ActiveSession[];
  dormancyDays: number;          // computed: days since last login

  // Identity fields (ITAR / CMMC / HIPAA) — USR-F1-T1. The server's answer,
  // passed through: a key is present iff the tenant's frameworks show it
  // (`{}` when none does), `null` when the framework state could not be
  // read. Classification / export-control access are the clearance rule's
  // derivation, never a separately stored grant. Nothing on this side
  // defaults or derives a value.
  identity: UserIdentity | null;

  // GDPR
  dataRegion: 'US' | 'EU' | 'APAC';
  gdprDataSubject: boolean;

  // Training & certifications
  training: TrainingCompletion[];

  // Governance
  lastReviewedAt: string | null;
  lastReviewedBy: string | null;
  nextReviewDue: string | null;
  riskScore: 'low' | 'medium' | 'high' | 'critical';

  // Activity (embedded summaries; full logs below)
  telemetry: UserTelemetry;
  activityLog: UserActivityLog[];
  auditTrail: UserAuditLog[];

  // Invitation (only present for status=invited)
  invitation: InvitationInfo | null;

  // Display
  avatarColor: string;           // derived from name hash for initials background
}

// ─── Role ───────────────────────────────────────────────────────────────────

export type Permission = string; // e.g., 'ai:chat:use', 'users:edit', 'agents:create'

export type ResourceKey =
  | 'ai_chat'
  | 'agents'
  | 'studio_tools'
  | 'studio_skills'
  | 'studio_prompts'
  | 'library'
  | 'automate'
  | 'models'
  | 'connections'
  | 'insights'
  | 'trust_overview'
  | 'trust_activity'
  | 'trust_frameworks'
  | 'trust_audit'
  | 'users'
  | 'groups'
  | 'roles'
  | 'invitations'
  | 'admin_security'
  | 'admin_health'
  | 'admin_settings'
  | 'admin_notifications'
  | 'admin_branding';

export type Action = 'view' | 'use' | 'create' | 'edit' | 'delete' | 'manage';

export interface Role {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;                  // system roles cannot be deleted
  permissions: Partial<Record<ResourceKey, Action[]>>;
  userCount: number;                  // denormalized
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// ─── Group ──────────────────────────────────────────────────────────────────

export type GroupType = 'security' | 'user_group' | 'dynamic';

export interface Group {
  id: string;
  name: string;
  description: string;
  type: GroupType;
  memberIds: string[];
  memberCount: number;                // denormalized
  rolesGrantedIds: string[];          // bulk role grants
  resourceGrants: string[];           // resource-specific grants
  dynamicQuery?: string;              // if type === 'dynamic'
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// ─── Department ─────────────────────────────────────────────────────────────

export interface Department {
  id: string;
  name: string;
  description: string;
  parentId: string | null;            // for hierarchy
  headUserId: string | null;          // department head
  memberIds: string[];
  memberCount: number;
  inheritedRoleIds: string[];         // roles auto-granted to all members
}

// ─── Activity / identity events (for Activity tab) ──────────────────────────

export type IdentityEventType =
  | 'login_succeeded'
  | 'login_failed'
  | 'logout'
  | 'password_changed'
  | 'password_reset_requested'
  | 'mfa_challenge_succeeded'
  | 'mfa_challenge_failed'
  | 'mfa_enabled'
  | 'mfa_reset'
  | 'session_started'
  | 'session_terminated_by_admin'
  | 'impersonation_started'
  | 'impersonation_ended'
  | 'account_locked'
  | 'account_unlocked'
  | 'clearance_granted'
  | 'clearance_revoked'
  | 'us_person_verified'
  | 'role_assigned'
  | 'role_removed';

export interface IdentityEvent {
  id: string;
  timestamp: string;
  type: IdentityEventType;
  userId: string;
  userName: string;
  actorId: string | null;             // null if self-initiated
  actorName: string | null;
  ip: string;
  userAgent: string;
  location: string;
  outcome: 'success' | 'failure';
  details: string;
  metadata?: Record<string, unknown>;
}

// ─── Stats types ────────────────────────────────────────────────────────────

export interface UsersStats {
  total: number;
  active: number;
  invited: number;
  locked: number;
  suspended: number;
  deactivated: number;
  mfaEnabled: number;
  usPersonVerified: number;
  reviewOverdue: number;
  dormant30d: number;
}

// ─── Mock departments ───────────────────────────────────────────────────────

export const DEPARTMENTS: Department[] = [
  {
    id: 'dept-operations', name: 'Operations', description: 'Plant operations and shift coordination',
    parentId: null, headUserId: 'user-sarah-chen', memberIds: [], memberCount: 12,
    inheritedRoleIds: ['role-member'],
  },
  {
    id: 'dept-maintenance', name: 'Maintenance', description: 'Equipment maintenance and reliability',
    parentId: null, headUserId: 'user-carlos-reyes', memberIds: [], memberCount: 23,
    inheritedRoleIds: ['role-member'],
  },
  {
    id: 'dept-engineering', name: 'Engineering', description: 'Process engineering and design',
    parentId: null, headUserId: null, memberIds: [], memberCount: 14,
    inheritedRoleIds: ['role-member'],
  },
  {
    id: 'dept-compliance', name: 'Compliance & Legal', description: 'Regulatory compliance and legal affairs',
    parentId: null, headUserId: 'user-mike-rodriguez', memberIds: [], memberCount: 7,
    inheritedRoleIds: ['role-power_user'],
  },
  {
    id: 'dept-safety', name: 'Safety', description: 'Occupational safety and safety governance',
    parentId: null, headUserId: 'user-robert-taylor', memberIds: [], memberCount: 9,
    inheritedRoleIds: ['role-member'],
  },
  {
    id: 'dept-procurement', name: 'Procurement', description: 'Parts and vendor procurement',
    parentId: null, headUserId: null, memberIds: [], memberCount: 12,
    inheritedRoleIds: ['role-member'],
  },
  {
    id: 'dept-it', name: 'IT & Security', description: 'IT infrastructure and cybersecurity',
    parentId: null, headUserId: 'user-james-kim', memberIds: [], memberCount: 8,
    inheritedRoleIds: ['role-power_user'],
  },
  {
    id: 'dept-executive', name: 'Executive', description: 'Executive leadership',
    parentId: null, headUserId: null, memberIds: [], memberCount: 3,
    inheritedRoleIds: ['role-admin'],
  },
];

// ─── Mock roles ─────────────────────────────────────────────────────────────

export const ROLES: Role[] = [
  {
    id: 'role-admin',
    name: 'Administrator',
    description: 'Full tenant control — manages users, settings, and compliance',
    isSystem: true,
    userCount: 4,
    createdAt: daysAgo(180).toISOString(),
    updatedAt: daysAgo(180).toISOString(),
    createdBy: 'system',
    permissions: {
      ai_chat: ['view', 'use'],
      agents: ['view', 'use', 'create', 'edit', 'delete', 'manage'],
      studio_tools: ['view', 'use', 'create', 'edit', 'delete'],
      studio_skills: ['view', 'use', 'create', 'edit', 'delete'],
      studio_prompts: ['view', 'use', 'create', 'edit', 'delete'],
      library: ['view', 'use', 'create', 'edit', 'delete', 'manage'],
      automate: ['view', 'use', 'create', 'edit', 'delete', 'manage'],
      models: ['view', 'manage'],
      connections: ['view', 'create', 'edit', 'delete', 'manage'],
      insights: ['view'],
      trust_overview: ['view'],
      trust_activity: ['view'],
      trust_frameworks: ['view', 'edit', 'manage'],
      trust_audit: ['view'],
      users: ['view', 'create', 'edit', 'delete', 'manage'],
      groups: ['view', 'create', 'edit', 'delete'],
      roles: ['view', 'create', 'edit', 'delete'],
      invitations: ['view', 'create', 'delete'],
      admin_security: ['view', 'edit'],
      admin_health: ['view'],
      admin_settings: ['view', 'edit'],
      admin_notifications: ['view', 'edit'],
      admin_branding: ['view', 'edit'],
    },
  },
  {
    id: 'role-power_user',
    name: 'Power User',
    description: 'Can create custom agents, manage documents, and use all features',
    isSystem: true,
    userCount: 18,
    createdAt: daysAgo(180).toISOString(),
    updatedAt: daysAgo(30).toISOString(),
    createdBy: 'system',
    permissions: {
      ai_chat: ['view', 'use'],
      agents: ['view', 'use', 'create', 'edit'],
      studio_tools: ['view', 'use', 'create', 'edit'],
      studio_skills: ['view', 'use', 'create', 'edit'],
      studio_prompts: ['view', 'use', 'create', 'edit'],
      library: ['view', 'use', 'create', 'edit'],
      automate: ['view', 'use', 'create', 'edit'],
      models: ['view'],
      connections: ['view'],
      insights: ['view'],
      trust_overview: ['view'],
      trust_activity: ['view'],
    },
  },
  {
    id: 'role-member',
    name: 'Member',
    description: 'Can use Ask Argus, browse library, invoke agents',
    isSystem: true,
    userCount: 38,
    createdAt: daysAgo(180).toISOString(),
    updatedAt: daysAgo(180).toISOString(),
    createdBy: 'system',
    permissions: {
      ai_chat: ['view', 'use'],
      agents: ['view', 'use'],
      studio_tools: ['view'],
      studio_skills: ['view'],
      studio_prompts: ['view'],
      library: ['view', 'use'],
      automate: ['view', 'use'],
      insights: ['view'],
    },
  },
  {
    id: 'role-viewer',
    name: 'Viewer',
    description: 'Read-only. Can view dashboards, documents, agent configs',
    isSystem: true,
    userCount: 11,
    createdAt: daysAgo(180).toISOString(),
    updatedAt: daysAgo(180).toISOString(),
    createdBy: 'system',
    permissions: {
      ai_chat: ['view'],
      agents: ['view'],
      library: ['view'],
      insights: ['view'],
      trust_overview: ['view'],
    },
  },
  {
    id: 'role-compliance_officer',
    name: 'Compliance Officer',
    description: 'Custom: compliance officer with Trust admin rights',
    isSystem: false,
    userCount: 3,
    createdAt: daysAgo(45).toISOString(),
    updatedAt: daysAgo(15).toISOString(),
    createdBy: 'user-sarah-chen',
    permissions: {
      ai_chat: ['view', 'use'],
      agents: ['view', 'use'],
      library: ['view', 'use'],
      trust_overview: ['view'],
      trust_activity: ['view'],
      trust_frameworks: ['view', 'edit', 'manage'],
      trust_audit: ['view'],
      insights: ['view'],
    },
  },
];

// ─── Mock groups ────────────────────────────────────────────────────────────

export const GROUPS: Group[] = [
  {
    id: 'group-itar-authorized',
    name: 'ITAR-Authorized Users',
    description: 'US Persons with ITAR clearance and classification access',
    type: 'dynamic',
    memberIds: [],
    memberCount: 23,
    rolesGrantedIds: [],
    resourceGrants: [],
    dynamicQuery: 'usPerson.verified = true AND clearance.level >= "confidential"',
    createdAt: daysAgo(120).toISOString(),
    updatedAt: daysAgo(7).toISOString(),
    createdBy: 'user-mike-rodriguez',
  },
  {
    id: 'group-night-shift',
    name: 'Night Shift Ops',
    description: 'Operations team members on the night shift rotation',
    type: 'user_group',
    memberIds: [],
    memberCount: 8,
    rolesGrantedIds: ['role-member'],
    resourceGrants: ['agent-shift-handover'],
    createdAt: daysAgo(90).toISOString(),
    updatedAt: daysAgo(14).toISOString(),
    createdBy: 'user-sarah-chen',
  },
  {
    id: 'group-compliance-reviewers',
    name: 'Compliance Reviewers',
    description: 'Users authorized to sign off on compliance attestations',
    type: 'security',
    memberIds: [],
    memberCount: 5,
    rolesGrantedIds: ['role-compliance_officer'],
    resourceGrants: [],
    createdAt: daysAgo(60).toISOString(),
    updatedAt: daysAgo(5).toISOString(),
    createdBy: 'user-sarah-chen',
  },
  {
    id: 'group-phi-authorized',
    name: 'PHI-Authorized',
    description: 'HIPAA workforce members with PHI access',
    type: 'security',
    memberIds: [],
    memberCount: 4,
    rolesGrantedIds: ['role-power_user'],
    resourceGrants: [],
    createdAt: daysAgo(200).toISOString(),
    updatedAt: daysAgo(30).toISOString(),
    createdBy: 'user-priya-patel',
  },
  {
    id: 'group-new-hires-q2',
    name: 'New Hires Q2 2026',
    description: 'Users onboarded this quarter — require supervised access',
    type: 'user_group',
    memberIds: [],
    memberCount: 6,
    rolesGrantedIds: ['role-viewer'],
    resourceGrants: [],
    createdAt: daysAgo(45).toISOString(),
    updatedAt: daysAgo(2).toISOString(),
    createdBy: 'user-james-kim',
  },
];

// ─── Avatar color helper ────────────────────────────────────────────────────

const AVATAR_COLORS = ['indigo', 'violet', 'cyan', 'emerald', 'amber', 'red', 'blue', 'pink', 'orange', 'teal'];
function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

// ─── User builder ───────────────────────────────────────────────────────────

interface UserSeed {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  /**
   * The ONE department this fixture puts the person in — a SEED input, NOT
   * the `User` shape. `buildUser` turns it into the set the rail renders
   * (`departments: { all, items }`); B2b-T3 changed the shape a screen reads,
   * not how many departments a fixture bothers to name.
   */
  department: string;
  position: string;
  employmentType: EmploymentType;
  managerId?: string | null;
  employeeId: string;
  roleIds: string[];
  clearance: ClearanceLevel;
  usPersonVerified: boolean;
  usPersonDocType?: UsPersonDocumentType;
  classificationAccess: ClassificationTier[];
  exportControlAccess: ExportControlTier[];
  mfaEnabled?: boolean;
  mfaMethod?: MfaMethod;
  ssoLinked?: boolean;
  ssoProvider?: string;
  status?: UserStatus;
  daysSinceJoined?: number;
  daysSinceLastLogin?: number;
}

function buildUser(seed: UserSeed): User {
  const dept = DEPARTMENTS.find(d => d.id === seed.department);
  const manager = seed.managerId ? null : null; // computed later
  const joinedDate = daysAgo(seed.daysSinceJoined ?? randInt(30, 700));
  const lastLoginAt = seed.status === 'invited' || seed.status === 'deactivated'
    ? null
    : daysAgo(seed.daysSinceLastLogin ?? randInt(0, 45));
  const dormancyDays = lastLoginAt
    ? Math.floor((NOW.getTime() - lastLoginAt.getTime()) / (86400 * 1000))
    : 0;

  const roles: RoleAssignment[] = seed.roleIds.map(rid => {
    const role = ROLES.find(r => r.id === rid)!;
    return {
      roleId: rid,
      roleName: role.name,
      source: dept?.inheritedRoleIds.includes(rid) ? 'department' : 'direct',
      grantedAt: joinedDate.toISOString(),
      grantedBy: 'user-sarah-chen',
      sourceDetail: dept?.inheritedRoleIds.includes(rid)
        ? `Inherited from department: ${dept.name}`
        : 'Directly assigned',
    };
  });

  const reviewIntervalDays = 90;
  const lastReviewedDate = daysAgo(randInt(10, 120));
  const nextReviewDate = new Date(lastReviewedDate.getTime() + reviewIntervalDays * 86400 * 1000);
  const reviewOverdue = nextReviewDate.getTime() < NOW.getTime();

  // Risk score: function of clearance, dormancy, admin role, review overdue
  const isAdmin = roles.some(r => r.roleId === 'role-admin');
  const isHighClearance =
    seed.clearance === 'secret' ||
    seed.clearance === 'ts' ||
    seed.clearance === 'ts_sci';
  let riskScore: 'low' | 'medium' | 'high' | 'critical' = 'low';
  if (isAdmin && reviewOverdue) riskScore = 'critical';
  else if (isAdmin && dormancyDays > 30) riskScore = 'high';
  else if (isHighClearance && reviewOverdue) riskScore = 'high';
  else if (dormancyDays > 60) riskScore = 'medium';
  else if (reviewOverdue) riskScore = 'medium';

  return {
    id: seed.id,
    email: seed.email,
    firstName: seed.firstName,
    lastName: seed.lastName,
    fullName: `${seed.firstName} ${seed.lastName}`,
    employeeId: seed.employeeId,
    phoneNumber: `+1 ${randInt(200, 999)}-${randInt(1000, 9999)}`,

    // B2b-T3 — the set shape, from the one seed department the fixture names.
    // A seeded person is always in exactly one department, so `all` is false.
    departments: {
      all: false,
      items: [{ id: seed.department, name: dept?.name ?? seed.department }],
    },
    position: seed.position,
    managerId: seed.managerId ?? null,
    managerName: null,
    employmentType: seed.employmentType,
    startDate: daysAgo((seed.daysSinceJoined ?? 180) + randInt(7, 21)).toISOString(),
    // B2c-T2 — a time-bounded engagement gets an end date, read from the
    // fact declared on the employment type rather than from one literal.
    endDate: isTimeBoundedEmployment(seed.employmentType) ? daysAgo(-90).toISOString() : null,
    costCenter: `CC-${randInt(100, 999)}`,

    status: seed.status ?? 'active',
    joinedDate: joinedDate.toISOString(),
    lastLoginAt: lastLoginAt?.toISOString() ?? null,
    createdAt: joinedDate.toISOString(),
    updatedAt: lastLoginAt?.toISOString() ?? joinedDate.toISOString(),

    mfaEnabled: seed.mfaEnabled ?? true,
    mfaMethod: seed.mfaMethod ?? 'totp',
    mfaEnforcedByPolicy: isAdmin,
    passwordLastChangedAt: daysAgo(randInt(15, 90)).toISOString(),
    passwordExpiresAt: daysAgo(-1 * randInt(5, 60)).toISOString(),
    ssoLinked: seed.ssoLinked ?? false,
    ssoProvider: seed.ssoProvider ?? null,

    roles,
    groupIds: [],
    activeSessions: [],
    dormancyDays,

    // USR-F1-T1 — a fixture person has no server answer for identity fields.
    identity: null,

    dataRegion: 'US',
    gdprDataSubject: false,

    training: generateTraining(seed),

    lastReviewedAt: lastReviewedDate.toISOString(),
    lastReviewedBy: 'Sarah Chen',
    nextReviewDue: nextReviewDate.toISOString(),
    riskScore,

    telemetry: generateTelemetry(seed),
    activityLog: generateActivityLog(seed.id, seed.firstName),
    auditTrail: generateAuditTrail(seed.id, seed.firstName),

    invitation: seed.status === 'invited' ? {
      invitedAt: daysAgo(randInt(1, 7)).toISOString(),
      invitedBy: 'user-sarah-chen',
      invitedByName: 'Sarah Chen',
      expiresAt: daysAgo(-1 * randInt(1, 7)).toISOString(),
      status: 'pending',
      inviteLink: `https://argusai.acme.com/invite/${seed.id}-xxxx`,
      preAssignedRoleIds: seed.roleIds,
      preAssignedGroupIds: [],
      preAssignedClearance: seed.clearance,
    } : null,

    avatarColor: avatarColor(`${seed.firstName} ${seed.lastName}`),
  };
}

function generateTraining(seed: UserSeed): TrainingCompletion[] {
  const out: TrainingCompletion[] = [];
  out.push({
    id: `training-${seed.id}-gen-1`,
    courseId: 'argus-platform-101',
    courseName: 'ArgusAI Platform Basics',
    completedAt: daysAgo(randInt(30, 300)).toISOString(),
    expiresAt: null,
    certificateUrl: '/certs/platform-basics.pdf',
    status: 'current',
  });
  if (seed.clearance !== 'unclassified') {
    const completedDaysAgo = randInt(180, 400);
    const expiresInDays = 365 - completedDaysAgo;
    out.push({
      id: `training-${seed.id}-itar`,
      courseId: 'itar-awareness-2025',
      courseName: 'ITAR Awareness & Controls',
      completedAt: daysAgo(completedDaysAgo).toISOString(),
      expiresAt: daysAgo(-1 * expiresInDays).toISOString(),
      certificateUrl: '/certs/itar-awareness.pdf',
      status: expiresInDays < 30 ? 'expiring_soon' : 'current',
    });
  }
  return out;
}

function generateTelemetry(seed: UserSeed): UserTelemetry {
  const base = seed.roleIds.includes('role-admin') ? 40
    : seed.roleIds.includes('role-power_user') ? 18
    : seed.roleIds.includes('role-viewer') ? 2 : 10;
  const q7 = Math.floor(base * (7 + rng() * 7));
  const q30 = Math.floor(q7 * (4 + rng() * 1.5));
  return {
    queriesLast7d: q7,
    queriesLast30d: q30,
    avgQueriesPerDay: Math.round(q30 / 30),
    topAgentId: 'agent-ask-argus',
    topAgentName: 'Ask Argus',
    documentsAccessedLast30d: Math.floor(q30 * 0.4),
    avgSessionDurationMin: randInt(18, 75),
    lastActive: daysAgo(randInt(0, 3)).toISOString(),
    consumedTokensLast30d: q30 * randInt(2800, 4500),
    estimatedCostLast30d: Math.round(q30 * 0.019 * 100) / 100,
  };
}

function generateActivityLog(userId: string, firstName: string): UserActivityLog[] {
  const out: UserActivityLog[] = [];
  for (let i = 0; i < 10; i++) {
    const type: UserActivityLog['type'] = (() => {
      const n = Math.floor(rng() * 100);
      if (n < 70) return 'login';
      if (n < 80) return 'mfa_challenge';
      if (n < 85) return 'logout';
      if (n < 90) return 'login_failed';
      if (n < 93) return 'password_changed';
      if (n < 96) return 'mfa_enabled';
      return 'session_terminated';
    })();
    out.push({
      id: `act-${userId}-${i}`,
      timestamp: daysAgo(i * 0.8).toISOString(),
      type,
      ip: `10.${randInt(0, 255)}.${randInt(0, 255)}.${randInt(1, 254)}`,
      userAgent: 'Mozilla/5.0 (Macintosh) Chrome/138.0',
      location: ['Houston, TX', 'Plano, TX', 'Austin, TX', 'San Antonio, TX'][i % 4] + ', US',
      outcome: type === 'login_failed' ? 'failure' : 'success',
      details: type === 'login' ? `${firstName} signed in successfully`
        : type === 'login_failed' ? 'Invalid password (attempt 1 of 5)'
        : type === 'mfa_challenge' ? 'MFA code validated'
        : type === 'logout' ? `${firstName} signed out`
        : type === 'password_changed' ? 'User changed their password'
        : type === 'mfa_enabled' ? 'TOTP enrolled for this account'
        : 'Session terminated by admin',
    });
  }
  return out;
}

function generateAuditTrail(userId: string, firstName: string): UserAuditLog[] {
  const out: UserAuditLog[] = [];
  const events = [
    { action: 'user_created', target: 'User record', actor: 'Sarah Chen' },
    { action: 'role_assigned', target: 'Role: Member', actor: 'Sarah Chen' },
    { action: 'clearance_granted', target: 'Clearance: Confidential', actor: 'James Anderson (Security Officer)' },
    { action: 'us_person_verified', target: 'US Person status', actor: 'Mike Rodriguez' },
    { action: 'mfa_enrolled', target: 'TOTP authenticator', actor: firstName + ' (self)' },
    { action: 'training_completed', target: 'ITAR Awareness & Controls', actor: firstName + ' (self)' },
    { action: 'access_reviewed', target: 'Quarterly access review', actor: 'Sarah Chen' },
    { action: 'session_terminated', target: 'All sessions', actor: 'James Kim' },
  ];
  for (let i = 0; i < Math.min(events.length, 6); i++) {
    out.push({
      id: `audit-${userId}-${i}`,
      timestamp: daysAgo(30 - i * 4).toISOString(),
      sequenceNumber: 4_827_000 + i,
      hash: `${i.toString(16).padStart(4, '0')}${'a'.repeat(60)}`,
      previousHash: `${(i - 1).toString(16).padStart(4, '0')}${'a'.repeat(60)}`,
      action: events[i].action,
      actor: 'user-admin',
      actorName: events[i].actor,
      target: events[i].target,
    });
  }
  return out;
}

// ─── User seeds (the main roster) ───────────────────────────────────────────

const USER_SEEDS: UserSeed[] = [
  // Admins
  {
    id: 'user-sarah-chen', firstName: 'Sarah', lastName: 'Chen', email: 'sarah.chen@acme.com',
    department: 'dept-executive', position: 'Chief Information Security Officer',
    employmentType: 'full_time', employeeId: 'EMP-0001', roleIds: ['role-admin'],
    clearance: 'ts', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui', 'restricted'], exportControlAccess: ['none', 'itar', 'ear'],
    mfaEnabled: true, mfaMethod: 'hardware_key', ssoLinked: true, ssoProvider: 'Okta',
    daysSinceJoined: 420, daysSinceLastLogin: 0,
  },
  {
    id: 'user-mike-rodriguez', firstName: 'Mike', lastName: 'Rodriguez', email: 'mike.rodriguez@acme.com',
    department: 'dept-compliance', position: 'Compliance Lead',
    employmentType: 'full_time', employeeId: 'EMP-0002', roleIds: ['role-admin', 'role-compliance_officer'],
    clearance: 'secret', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui', 'restricted'], exportControlAccess: ['none', 'itar'],
    mfaEnabled: true, mfaMethod: 'hardware_key', ssoLinked: true, ssoProvider: 'Okta',
    daysSinceJoined: 380, daysSinceLastLogin: 0,
  },
  {
    id: 'user-james-kim', firstName: 'James', lastName: 'Kim', email: 'james.kim@acme.com',
    department: 'dept-it', position: 'IT Director',
    employmentType: 'full_time', employeeId: 'EMP-0003', roleIds: ['role-admin'],
    clearance: 'secret', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui', 'restricted'], exportControlAccess: ['none', 'itar'],
    mfaEnabled: true, mfaMethod: 'hardware_key', ssoLinked: true, ssoProvider: 'Okta',
    daysSinceJoined: 560, daysSinceLastLogin: 1,
  },
  {
    id: 'user-lisa-wong', firstName: 'Lisa', lastName: 'Wong', email: 'lisa.wong@acme.com',
    department: 'dept-compliance', position: 'Legal Counsel',
    employmentType: 'full_time', employeeId: 'EMP-0004', roleIds: ['role-admin', 'role-compliance_officer'],
    clearance: 'secret', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: ['unclassified', 'cui', 'restricted'], exportControlAccess: ['none', 'itar'],
    mfaEnabled: true, mfaMethod: 'totp', ssoLinked: true, ssoProvider: 'Okta',
    daysSinceJoined: 210, daysSinceLastLogin: 2,
  },

  // Power users
  {
    id: 'user-priya-patel', firstName: 'Priya', lastName: 'Patel', email: 'priya.patel@acme.com',
    department: 'dept-compliance', position: 'Data Protection Officer',
    employmentType: 'full_time', employeeId: 'EMP-0010', roleIds: ['role-power_user', 'role-compliance_officer'],
    clearance: 'cui', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp', ssoLinked: true, ssoProvider: 'Okta',
    daysSinceJoined: 340, daysSinceLastLogin: 0,
  },
  {
    id: 'user-david-park', firstName: 'David', lastName: 'Park', email: 'david.park@acme.com',
    department: 'dept-it', position: 'Security Engineer',
    employmentType: 'full_time', employeeId: 'EMP-0011', roleIds: ['role-power_user'],
    clearance: 'secret', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui', 'restricted'], exportControlAccess: ['none', 'itar'],
    mfaEnabled: true, mfaMethod: 'hardware_key', ssoLinked: true, ssoProvider: 'Okta',
    daysSinceJoined: 290, daysSinceLastLogin: 0,
  },
  {
    id: 'user-elena-vasquez', firstName: 'Elena', lastName: 'Vasquez', email: 'elena.vasquez@acme.com',
    department: 'dept-it', position: 'Privacy Engineer',
    employmentType: 'full_time', employeeId: 'EMP-0012', roleIds: ['role-power_user'],
    clearance: 'cui', usPersonVerified: true, usPersonDocType: 'permanent_resident_card',
    classificationAccess: ['unclassified', 'cui'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp', ssoLinked: true, ssoProvider: 'Okta',
    daysSinceJoined: 180, daysSinceLastLogin: 0,
  },
  {
    id: 'user-carlos-reyes', firstName: 'Carlos', lastName: 'Reyes', email: 'carlos.reyes@acme.com',
    department: 'dept-operations', position: 'Operations Manager',
    employmentType: 'full_time', employeeId: 'EMP-0020', roleIds: ['role-power_user'],
    clearance: 'cui', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 470, daysSinceLastLogin: 1,
  },
  {
    id: 'user-robert-taylor', firstName: 'Robert', lastName: 'Taylor', email: 'robert.taylor@acme.com',
    department: 'dept-safety', position: 'Safety Lead',
    employmentType: 'full_time', employeeId: 'EMP-0030', roleIds: ['role-power_user'],
    clearance: 'cui', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: ['unclassified', 'cui'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 365, daysSinceLastLogin: 0,
  },
  {
    id: 'user-nadia-ahmed', firstName: 'Nadia', lastName: 'Ahmed', email: 'nadia.ahmed@acme.com',
    department: 'dept-maintenance', position: 'Reliability Engineer',
    employmentType: 'full_time', employeeId: 'EMP-0040', roleIds: ['role-power_user'],
    clearance: 'cui', usPersonVerified: false,  // Not yet verified
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 150, daysSinceLastLogin: 1,
  },

  // Members
  {
    id: 'user-alex-johnson', firstName: 'Alex', lastName: 'Johnson', email: 'alex.johnson@acme.com',
    department: 'dept-maintenance', position: 'Maintenance Technician',
    employmentType: 'full_time', employeeId: 'EMP-0050', roleIds: ['role-member'],
    clearance: 'unclassified', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 220, daysSinceLastLogin: 0,
  },
  {
    id: 'user-jessica-nguyen', firstName: 'Jessica', lastName: 'Nguyen', email: 'jessica.nguyen@acme.com',
    department: 'dept-engineering', position: 'Process Engineer',
    employmentType: 'full_time', employeeId: 'EMP-0051', roleIds: ['role-member'],
    clearance: 'cui', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 195, daysSinceLastLogin: 2,
  },
  {
    id: 'user-kevin-oneill', firstName: "Kevin", lastName: "O'Neill", email: 'kevin.oneill@acme.com',
    department: 'dept-operations', position: 'Shift Supervisor',
    employmentType: 'full_time', employeeId: 'EMP-0052', roleIds: ['role-member'],
    clearance: 'unclassified', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 640, daysSinceLastLogin: 5,
  },
  {
    id: 'user-maria-garcia', firstName: 'Maria', lastName: 'Garcia', email: 'maria.garcia@acme.com',
    department: 'dept-procurement', position: 'Parts Specialist',
    employmentType: 'full_time', employeeId: 'EMP-0053', roleIds: ['role-member'],
    clearance: 'unclassified', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 110, daysSinceLastLogin: 1,
  },

  // Dormant user (triggers "dormant" risk)
  {
    id: 'user-tom-wilson', firstName: 'Tom', lastName: 'Wilson', email: 'tom.wilson@acme.com',
    department: 'dept-maintenance', position: 'Field Technician',
    employmentType: 'full_time', employeeId: 'EMP-0060', roleIds: ['role-member'],
    clearance: 'unclassified', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: false, mfaMethod: 'none',  // No MFA — flag
    daysSinceJoined: 380, daysSinceLastLogin: 87,  // Dormant
  },

  // Contractors (end date present)
  {
    id: 'user-jordan-smith', firstName: 'Jordan', lastName: 'Smith', email: 'j.smith@contractor.com',
    department: 'dept-it', position: 'Infrastructure Consultant',
    employmentType: 'contractor', employeeId: 'CTR-0001', roleIds: ['role-power_user'],
    clearance: 'cui', usPersonVerified: true, usPersonDocType: 'us_passport',
    classificationAccess: ['unclassified', 'cui'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'hardware_key',
    daysSinceJoined: 60, daysSinceLastLogin: 0,
  },
  {
    id: 'user-ravi-kumar', firstName: 'Ravi', lastName: 'Kumar', email: 'r.kumar@datawerks.io',
    department: 'dept-engineering', position: 'Data Science Consultant',
    employmentType: 'vendor', employeeId: 'VEN-0001', roleIds: ['role-viewer'],
    clearance: 'unclassified', usPersonVerified: false,
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    daysSinceJoined: 30, daysSinceLastLogin: 3,
  },

  // Invited (pending onboarding)
  {
    id: 'user-invited-1', firstName: 'Marcus', lastName: 'Bennett', email: 'marcus.bennett@acme.com',
    department: 'dept-engineering', position: 'Senior Process Engineer',
    employmentType: 'full_time', employeeId: 'EMP-0070', roleIds: ['role-member'],
    clearance: 'unclassified', usPersonVerified: false,
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: false, mfaMethod: 'none',
    status: 'invited', daysSinceJoined: 3, daysSinceLastLogin: 0,
  },
  {
    id: 'user-invited-2', firstName: 'Aisha', lastName: 'Khalid', email: 'aisha.khalid@acme.com',
    department: 'dept-safety', position: 'Safety Engineer',
    employmentType: 'full_time', employeeId: 'EMP-0071', roleIds: ['role-member'],
    clearance: 'unclassified', usPersonVerified: false,
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: false, mfaMethod: 'none',
    status: 'invited', daysSinceJoined: 5, daysSinceLastLogin: 0,
  },

  // Locked (security incident)
  {
    id: 'user-locked-1', firstName: 'Derek', lastName: 'Monroe', email: 'derek.monroe@acme.com',
    department: 'dept-procurement', position: 'Procurement Analyst',
    employmentType: 'full_time', employeeId: 'EMP-0080', roleIds: ['role-member'],
    clearance: 'unclassified', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: ['unclassified'], exportControlAccess: ['none'],
    mfaEnabled: true, mfaMethod: 'totp',
    status: 'locked', daysSinceJoined: 240, daysSinceLastLogin: 12,
  },

  // Deactivated (offboarded)
  {
    id: 'user-deactivated-1', firstName: 'Hannah', lastName: 'Park', email: 'hannah.park@acme.com',
    department: 'dept-operations', position: 'Former Operator',
    employmentType: 'full_time', employeeId: 'EMP-0090', roleIds: [],
    clearance: 'unclassified', usPersonVerified: true, usPersonDocType: 'drivers_license_ssn',
    classificationAccess: [], exportControlAccess: [],
    mfaEnabled: false, mfaMethod: 'none',
    status: 'deactivated', daysSinceJoined: 500, daysSinceLastLogin: 120,
  },
];

// ─── Build USERS ────────────────────────────────────────────────────────────

export const USERS: User[] = USER_SEEDS.map(buildUser);

// Second pass: populate manager names (now that USERS exists)
//
// B2b-T3 — the fixture reads the SET. `u.department` (one id) is gone from the
// `User` shape, so "is this person in department X" is asked of the list, the
// way `isInDepartment` asks it on the real data.
const seededIn = (u: User, departmentId: string): boolean =>
  u.departments.items.some(d => d.id === departmentId);
for (const u of USERS) {
  if (u.managerId) {
    const m = USERS.find(x => x.id === u.managerId);
    u.managerName = m?.fullName ?? null;
  } else if (seededIn(u, 'dept-operations')) {
    u.managerId = 'user-carlos-reyes';
    u.managerName = 'Carlos Reyes';
  } else if (seededIn(u, 'dept-maintenance')) {
    u.managerId = 'user-carlos-reyes';
    u.managerName = 'Carlos Reyes';
  } else if (seededIn(u, 'dept-compliance')) {
    u.managerId = 'user-mike-rodriguez';
    u.managerName = 'Mike Rodriguez';
  } else if (seededIn(u, 'dept-it')) {
    u.managerId = 'user-james-kim';
    u.managerName = 'James Kim';
  } else if (seededIn(u, 'dept-safety')) {
    u.managerId = 'user-robert-taylor';
    u.managerName = 'Robert Taylor';
  }
}

// Populate group memberships + active sessions
const groupMemberships: Record<string, string[]> = {
  // USR-F1-T1 — a fixture person carries no identity answer; the fixture's
  // group is drawn from the seed facts instead.
  'group-itar-authorized': USER_SEEDS.filter(s => s.usPersonVerified && clearanceRequiresUsPerson(s.clearance)).map(s => s.id),
  'group-night-shift': ['user-alex-johnson', 'user-kevin-oneill', 'user-tom-wilson'],
  'group-compliance-reviewers': ['user-sarah-chen', 'user-mike-rodriguez', 'user-lisa-wong', 'user-priya-patel'],
  'group-phi-authorized': ['user-priya-patel', 'user-lisa-wong'],
  'group-new-hires-q2': ['user-jordan-smith', 'user-ravi-kumar', 'user-invited-1', 'user-invited-2', 'user-elena-vasquez'],
};
for (const g of GROUPS) {
  const members = groupMemberships[g.id] ?? [];
  g.memberIds = members;
  g.memberCount = members.length;
  for (const uid of members) {
    const u = USERS.find(x => x.id === uid);
    if (u && !u.groupIds.includes(g.id)) u.groupIds.push(g.id);
  }
}

// Populate department member lists
for (const d of DEPARTMENTS) {
  d.memberIds = USERS.filter(u => seededIn(u, d.id)).map(u => u.id);
  d.memberCount = d.memberIds.length;
}

// Active sessions for currently-signed-in users
const sessionDevices = [
  { device: 'MacBook Pro · Chrome 138', deviceType: 'desktop' as const },
  { device: 'iPhone 15 Pro · Safari', deviceType: 'mobile' as const },
  { device: 'Windows 11 · Edge 138', deviceType: 'desktop' as const },
];
const sessionLocations = ['Houston, TX, US', 'Plano, TX, US', 'Austin, TX, US'];
for (const u of USERS) {
  if (u.status !== 'active') continue;
  const count = u.roles.some(r => r.roleId === 'role-admin') ? randInt(1, 3) : randInt(0, 2);
  for (let i = 0; i < count; i++) {
    const dev = sessionDevices[i % sessionDevices.length];
    u.activeSessions.push({
      id: `sess-${u.id}-${i}`,
      device: dev.device,
      deviceType: dev.deviceType,
      ip: `10.${randInt(0, 255)}.${randInt(0, 255)}.${randInt(1, 254)}`,
      location: sessionLocations[i % sessionLocations.length],
      startedAt: daysAgo(randInt(0, 2) + rng()).toISOString(),
      lastActiveAt: daysAgo(rng() * 0.1).toISOString(),
      current: i === 0,
    });
  }
}

// ─── Update role user counts ────────────────────────────────────────────────

for (const r of ROLES) {
  r.userCount = USERS.filter(u => u.roles.some(ra => ra.roleId === r.id)).length;
}

// ─── Stats ──────────────────────────────────────────────────────────────────

export const USERS_STATS: UsersStats = {
  total: USERS.length,
  active: USERS.filter(u => u.status === 'active').length,
  invited: USERS.filter(u => u.status === 'invited').length,
  locked: USERS.filter(u => u.status === 'locked').length,
  suspended: USERS.filter(u => u.status === 'suspended').length,
  deactivated: USERS.filter(u => u.status === 'deactivated').length,
  mfaEnabled: USERS.filter(u => u.mfaEnabled).length,
  usPersonVerified: USERS.filter(u => u.identity?.usPersonVerified === true).length,
  reviewOverdue: USERS.filter(u => u.nextReviewDue && new Date(u.nextReviewDue) < NOW).length,
  dormant30d: USERS.filter(u => u.dormancyDays >= 30 && u.status === 'active').length,
};

// ─── Style tokens ───────────────────────────────────────────────────────────

// Exhaustive over the SHARED alphabet: `Record<UserStatus, …>` means a state
// added to the table is a compile error here rather than an undefined lookup
// and a blank badge in front of an operator.
export const USER_STATUS_STYLES: Record<UserStatus, { label: string; color: string }> = {
  active:           { label: 'Active',      color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  invited:          { label: 'Invited',     color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  invited_revoked:  { label: 'Invite revoked', color: 'bg-muted text-muted-foreground border-border' },
  locked:           { label: 'Locked',      color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  suspended:        { label: 'Suspended',   color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
  deactivated:      { label: 'Deactivated', color: 'bg-muted text-muted-foreground border-border' },
  deleted:          { label: 'Deleted',     color: 'bg-muted text-muted-foreground border-border' },
};

/**
 * The badge COLOUR per clearance — presentation, and nothing else.
 *
 * USR-F1-T1: the `label` is read from `CLEARANCE_LABELS` in
 * `@argusai/shared` (compliance.schema.ts), the one vocabulary — the
 * B2c-T2 `EMPLOYMENT_TYPE_STYLES` precedent below.
 */
export const CLEARANCE_STYLES: Record<ClearanceLevel, { label: string; color: string }> = {
  unclassified: { label: CLEARANCE_LABELS.unclassified, color: 'bg-muted text-muted-foreground border-border' },
  cui:          { label: CLEARANCE_LABELS.cui,          color: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30' },
  secret:       { label: CLEARANCE_LABELS.secret,       color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30' },
  ts:           { label: CLEARANCE_LABELS.ts,           color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
  ts_sci:       { label: CLEARANCE_LABELS.ts_sci,       color: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30' },
};

/**
 * The badge COLOUR per employment type — presentation, and nothing else.
 *
 * B2c-T2: the `label` is not declared here. It is read from
 * `EMPLOYMENT_TYPE_LABELS` in `@argusai/shared`, the same list
 * `GET /users/field-options` serves to the dropdowns, so the badge and the
 * dropdown cannot name one value two ways. `Record<EmploymentType, …>` keys
 * this off the shared alphabet, so a seventh employment type fails the
 * typecheck here instead of rendering an empty badge.
 */
export const EMPLOYMENT_TYPE_STYLES: Record<EmploymentType, { label: string; color: string }> = {
  full_time:  { label: EMPLOYMENT_TYPE_LABELS.full_time,  color: 'bg-primary/10 text-primary border-primary/30' },
  part_time:  { label: EMPLOYMENT_TYPE_LABELS.part_time,  color: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30' },
  contractor: { label: EMPLOYMENT_TYPE_LABELS.contractor, color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  intern:     { label: EMPLOYMENT_TYPE_LABELS.intern,     color: 'bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/30' },
  vendor:     { label: EMPLOYMENT_TYPE_LABELS.vendor,     color: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30' },
  partner:    { label: EMPLOYMENT_TYPE_LABELS.partner,    color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30' },
};

export const RISK_SCORE_STYLES: Record<'low' | 'medium' | 'high' | 'critical', { label: string; color: string }> = {
  low:      { label: 'Low',      color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  medium:   { label: 'Medium',   color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  high:     { label: 'High',     color: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30' },
  critical: { label: 'Critical', color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
};

export const MFA_METHOD_LABEL: Record<MfaMethod, string> = {
  none:         'Not enrolled',
  totp:         'TOTP authenticator',
  webauthn:     'WebAuthn / Passkey',
  sms:          'SMS code',
  hardware_key: 'Hardware key (FIDO2)',
  email_otp:    'Email OTP',
};

export const CLASSIFICATION_LABEL: Record<ClassificationTier, string> = {
  unclassified: 'Unclassified',
  cui:          'CUI',
  restricted:   'Restricted',
};

export const EXPORT_CONTROL_LABEL: Record<ExportControlTier, string> = {
  none: 'None',
  itar: 'ITAR',
  ear:  'EAR',
};

// ─── Identity events (for Activity tab) ─────────────────────────────────────

export const IDENTITY_EVENTS: IdentityEvent[] = (() => {
  const out: IdentityEvent[] = [];
  let seq = 1_000;
  const now = NOW.getTime();
  for (let i = 0; i < 180; i++) {
    const user = USERS[randInt(0, USERS.length - 1)];
    if (user.status !== 'active' && user.status !== 'invited') continue;
    const type = (() => {
      const n = rng();
      if (n < 0.60) return 'login_succeeded';
      if (n < 0.68) return 'login_failed';
      if (n < 0.72) return 'logout';
      if (n < 0.80) return 'mfa_challenge_succeeded';
      if (n < 0.83) return 'mfa_challenge_failed';
      if (n < 0.88) return 'password_changed';
      if (n < 0.92) return 'mfa_enabled';
      if (n < 0.95) return 'session_terminated_by_admin';
      if (n < 0.97) return 'account_locked';
      if (n < 0.99) return 'role_assigned';
      return 'impersonation_started';
    })() as IdentityEventType;
    const hoursAgo = rng() * 168;   // up to 7 days
    const ts = new Date(now - hoursAgo * 3600 * 1000).toISOString();
    out.push({
      id: `ide-${seq++}`,
      timestamp: ts,
      type,
      userId: user.id,
      userName: user.fullName,
      actorId: type === 'role_assigned' || type === 'session_terminated_by_admin' || type === 'impersonation_started' ? 'user-sarah-chen' : null,
      actorName: type === 'role_assigned' || type === 'session_terminated_by_admin' || type === 'impersonation_started' ? 'Sarah Chen' : null,
      ip: `10.${randInt(0, 255)}.${randInt(0, 255)}.${randInt(1, 254)}`,
      userAgent: 'Mozilla/5.0 (Macintosh) Chrome/138.0',
      location: ['Houston, TX', 'Plano, TX', 'Austin, TX', 'San Antonio, TX'][i % 4] + ', US',
      outcome: type.includes('failed') ? 'failure' : 'success',
      details: eventDetails(type, user.fullName),
    });
  }
  return out.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
})();

function eventDetails(type: IdentityEventType, userName: string): string {
  switch (type) {
    case 'login_succeeded':      return `${userName} signed in successfully`;
    case 'login_failed':         return `Invalid password (attempt ${randInt(1, 3)} of 5)`;
    case 'logout':               return `${userName} signed out`;
    case 'password_changed':     return `${userName} changed their password`;
    case 'mfa_challenge_succeeded': return 'MFA challenge validated';
    case 'mfa_challenge_failed': return `MFA challenge failed`;
    case 'mfa_enabled':          return 'TOTP authenticator enrolled';
    case 'mfa_reset':            return 'MFA was reset by admin';
    case 'session_started':      return 'New session established';
    case 'session_terminated_by_admin': return 'Session terminated by administrator';
    case 'impersonation_started': return 'Admin started impersonating this user';
    case 'impersonation_ended':  return 'Admin ended impersonation';
    case 'account_locked':       return 'Account locked after 5 failed login attempts';
    case 'account_unlocked':     return 'Account unlocked by administrator';
    case 'clearance_granted':    return 'Clearance level granted';
    case 'clearance_revoked':    return 'Clearance level revoked';
    case 'us_person_verified':   return 'US Person status verified';
    case 'role_assigned':        return 'Role assigned';
    case 'role_removed':         return 'Role removed';
    case 'password_reset_requested': return `${userName} requested password reset`;
  }
}
